/**
 * Mapeia eventos internos (XegueiEventName) para os formatos esperados
 * por Meta Pixel e GA4. Centraliza naming/normalização para garantir
 * consistência entre adapters e CAPI server-side.
 */
import { AnalyticsEventParams, AnalyticsItem, XegueiEventName } from './types';

/** Eventos Meta Pixel padrão (https://developers.facebook.com/docs/meta-pixel/reference). */
export const META_EVENT_MAP: Record<XegueiEventName, string> = {
  PageView: 'PageView',
  ViewContent: 'ViewContent',
  AddToCart: 'AddToCart',
  RemoveFromCart: 'RemoveFromCart', // custom (não é standard)
  InitiateCheckout: 'InitiateCheckout',
  AddPaymentInfo: 'AddPaymentInfo',
  Purchase: 'Purchase',
  Lead: 'Lead',
  Search: 'Search',
  AbandonCheckout: 'AbandonCheckout', // custom
};

/** Eventos GA4 (recommended events). */
export const GA4_EVENT_MAP: Record<XegueiEventName, string> = {
  PageView: 'page_view',
  ViewContent: 'view_item',
  AddToCart: 'add_to_cart',
  RemoveFromCart: 'remove_from_cart',
  InitiateCheckout: 'begin_checkout',
  AddPaymentInfo: 'add_payment_info',
  Purchase: 'purchase',
  Lead: 'generate_lead',
  Search: 'search',
  AbandonCheckout: 'abandon_checkout',
};

function toGa4Items(items?: AnalyticsItem[]) {
  if (!items || !items.length) return undefined;
  return items.map((i) => ({
    item_id: i.id,
    item_name: i.nome,
    item_category: i.categoria,
    price: i.valor,
    quantity: i.quantidade || 1,
  }));
}

function toMetaContents(items?: AnalyticsItem[]) {
  if (!items || !items.length) return undefined;
  return items.map((i) => ({
    id: i.id,
    quantity: i.quantidade || 1,
    item_price: i.valor,
  }));
}

export const EventMapper = {
  /** Mapeia parâmetros para o formato esperado pelo Meta Pixel. */
  toMetaParams(params: AnalyticsEventParams): Record<string, any> {
    const out: Record<string, any> = {};
    if (params.value !== undefined) out.value = params.value;
    if (params.currency) out.currency = params.currency;
    if (params.items && params.items.length) {
      out.contents = toMetaContents(params.items);
      out.content_type = 'product';
      out.content_ids = params.items.map((i) => i.id).filter(Boolean);
    }
    if (params.orderId) out.order_id = params.orderId;
    if (params.searchTerm) out.search_string = params.searchTerm;
    if (params.coupon) out.coupon = params.coupon;
    if (params.extras) Object.assign(out, params.extras);
    return out;
  },

  /** Mapeia parâmetros para o formato esperado pelo GA4. */
  toGa4Params(params: AnalyticsEventParams): Record<string, any> {
    const out: Record<string, any> = {};
    if (params.value !== undefined) out.value = params.value;
    if (params.currency) out.currency = params.currency;
    const items = toGa4Items(params.items);
    if (items) out.items = items;
    if (params.orderId) out.transaction_id = params.orderId;
    if (params.searchTerm) out.search_term = params.searchTerm;
    if (params.coupon) out.coupon = params.coupon;
    if (params.paymentType) out.payment_type = params.paymentType;
    if (params.shipping !== undefined) out.shipping = params.shipping;
    if (params.discount !== undefined) out.discount = params.discount;
    if (params.eventSourceUrl) out.page_location = params.eventSourceUrl;
    if (params.extras) Object.assign(out, params.extras);
    return out;
  },
};

export default EventMapper;
